import styled from '@olxui/core/dist/theme/styled';
import { css } from '@emotion/core';
import { Button } from '@olxui/core/dist/core/Button';
import { DayPicker, DateFormatter } from 'react-day-picker';
import { format } from 'date-fns';

export const StyledDayPicker = styled(DayPicker)<{
    displayBlockDatesFeature: boolean;
    isSelectedDateInBlockedDates: boolean;
    isStr: boolean;
    dateFrom: boolean;
    isDateFromInCheckins: boolean;
}>`
    ${({
        theme,
        displayBlockDatesFeature,
        isSelectedDateInBlockedDates,
        isStr,
        dateFrom,
        isDateFromInCheckins
    }) => css`
        --rdp-cell-size: 30px;
        --rdp-accent-color: ${theme.colors['background-brand-alternative']};
        --rdp-background-color: ${theme.colors['background-brand-alternative']};
        --rdp-outline: 2px solid var(--rdp-accent-color);
        --rdp-outline-selected: 2px solid
            ${theme.colors['background-brand-alternative']};

        color: ${theme.colors['text-global-primary']};
        user-select: none;
        margin: 0 0 16px 0 !important;
        padding: 0 0 10px 0;
        background-color: ${theme.colors['background-brand-fields-disabled']};

        .rdp-caption {
            margin-bottom: 0.5em;
            padding: 0.5em 0.5em 0.5em 1em;
            background-color: ${theme.colors['background-global-secondary']};
        }

        .rdp-table {
            margin: 0 auto;
        }

        .rdp-month {
            width: 100%;
        }

        .rdp-caption_label {
            font-size: 14px;
            letter-spacing: 0.8px;
            text-transform: uppercase;
        }

        .rdp-head {
            margin-top: 1em;
        }

        .rdp-head_row {
            margin-bottom: 16px;
        }

        .rdp-head_cell {
            color: ${theme.colors['text-global-disabled']};
            font-size: 14px;
        }

        .rdp-day {
            border: ${displayBlockDatesFeature
                ? `1px solid ${theme.colors['background-brand-fields-disabled']}`
                : ''};
            border-radius: ${displayBlockDatesFeature && isStr ? '0' : '50%'};
            padding: 6px;
            font-size: 14px;
            font-weight: 700;

            &:not([aria-disabled='true']) {
                &:hover {
                    background: ${displayBlockDatesFeature && isStr
                        ? dateFrom
                            ? `linear-gradient(135deg, ${theme.colors['background-brand-notification-error']} 50%,  ${theme.colors['background-brand-fields-disabled']} 50%)`
                            : `linear-gradient(135deg, ${theme.colors['background-brand-fields-disabled']} 50%, ${theme.colors['background-brand-notification-error']} 50%)`
                        : displayBlockDatesFeature
                        ? theme.colors['background-brand-notification-error']
                        : theme.colors['background-brand-tooltip']};
                }
            }

            &[aria-disabled='true'] {
                color: ${theme.colors['text-global-disabled']};
                cursor: auto;

                &:hover {
                    background-color: transparent;
                }
            }

            &.booked {
                text-decoration: line-through;
            }
        }

        .rdp-day_selected {
            background-color: ${displayBlockDatesFeature
                ? isSelectedDateInBlockedDates
                    ? theme.colors['$04_teal_tint_dark']
                    : theme.colors['$04_red_tint_dark']
                : theme.colors['background-brand-tooltip']};
            &[aria-pressed='true'] {
                color: ${theme.colors['text-global-primary']};
            }
        }

        .rdp-nav_button {
            &:hover {
                &:not([disabled]) {
                    &:not(.rdp-day_selected) {
                        background-color: transparent;
                    }
                }
            }
        }

        .rdp-day_disabled {
            font-weight: 500;
            text-decoration: line-through;
            &:not([aria-disabled='true']) {
                &:hover {
                    background-color: transparent;
                }
            }
        }

        .rdp-day_today {
            font-weight: 500;
        }

        .rdp-day_range_start,
        .rdp-day_range_middle,
        .rdp-day_range_end {
            border-radius: 0;
        }

        .rdp-day_range_start.day_range_end {
            border-radius: 50%;
        }

        .rdp-day_range_start {
            border-top-left-radius: 50%;
            border-bottom-left-radius: 50%;
        }

        .rdp-day_range_end {
            border-top-right-radius: 50%;
            border-bottom-right-radius: 50%;
        }

        .booking-blocked-dates {
            background-color: ${theme.colors[
                'background-brand-notification-error'
            ]};
            border-radius: ${isStr ? '0' : '50%'};
            &[aria-pressed='true'] {
                color: ${isSelectedDateInBlockedDates
                    ? theme.colors['text-global-primary']
                    : theme.colors['text-global-inverse']} !important;
                background-color: ${isSelectedDateInBlockedDates
                    ? theme.colors['$04_teal_tint_dark']
                    : theme.colors['$04_red_tint_dark']} !important;
                border-radius: ${isSelectedDateInBlockedDates
                    ? isStr
                        ? 'inherit'
                        : '0'
                    : 'inherit'};
            }
            &:hover {
                background: ${isSelectedDateInBlockedDates
                    ? isStr
                        ? `linear-gradient(135deg, ${theme.colors['background-brand-notification-success']} 50%, ${theme.colors['background-brand-notification-error']} 50%)`
                        : theme.colors['background-brand-notification-success']
                    : isStr
                    ? !dateFrom
                        ? `linear-gradient(135deg, ${theme.colors['background-brand-notification-error']} 50%, ${theme.colors['background-brand-notification-success']} 50%)`
                        : theme.colors['background-brand-notification-error']
                    : theme.colors[
                          'background-brand-notification-error'
                      ]} !important;
            }
        }

        .booking-checkin-date {
            background: ${displayBlockDatesFeature
                ? isStr
                    ? `linear-gradient(135deg, ${theme.colors['background-brand-notification-error']} 50%, ${theme.colors['background-brand-fields-disabled']} 50%)`
                    : theme.colors['background-brand-notification-error']
                : 'inherit'} !important;
            border-radius: ${displayBlockDatesFeature && isStr
                ? '0'
                : '50%'} !important;
            &:hover {
                border-radius: ${displayBlockDatesFeature && isStr
                    ? '0'
                    : '50%'} !important;
                background: ${isSelectedDateInBlockedDates
                    ? isStr
                        ? `linear-gradient(135deg, ${theme.colors['background-brand-notification-success']} 50%, ${theme.colors['background-brand-fields-disabled']} 50%)`
                        : theme.colors['background-brand-notification-success']
                    : displayBlockDatesFeature
                    ? theme.colors['background-brand-notification-error']
                    : '#e7edff'} !important;
            }

            &[aria-pressed='true'] {
                background: ${displayBlockDatesFeature
                    ? isSelectedDateInBlockedDates
                        ? isStr
                            ? `linear-gradient(135deg, ${theme.colors['$04_teal_tint_dark']} 50%, ${theme.colors['background-brand-fields-disabled']} 50%)`
                            : theme.colors['$04_teal_tint_dark']
                        : `${theme.colors['$04_red_tint_dark']}`
                    : theme.colors['background-brand-tooltip']} !important;
                border-top-right-radius: 0 !important;
                border-bottom-right-radius: 0 !important;
            }
        }

        .booking-checkout-date {
            background: ${displayBlockDatesFeature
                ? isStr
                    ? `linear-gradient(135deg, ${theme.colors['background-brand-fields-disabled']} 50%, ${theme.colors['background-brand-notification-error']} 50%)`
                    : theme.colors['background-brand-notification-error']
                : 'inherit'} !important;
            border-radius: ${isStr ? '0' : '50%'} !important;
            &:hover {
                border-radius: ${isStr ? '0' : '50%'} !important;
                background: ${isSelectedDateInBlockedDates
                    ? isStr
                        ? `linear-gradient(135deg, ${theme.colors['background-brand-fields-disabled']} 50%, ${theme.colors['background-brand-notification-error']} 50%)`
                        : theme.colors['background-brand-notification-success']
                    : isStr
                    ? dateFrom
                        ? `linear-gradient(135deg, ${theme.colors['background-brand-notification-error']} 50%, ${theme.colors['background-brand-notification-error']} 50%)`
                        : `linear-gradient(135deg, ${theme.colors['background-brand-fields-disabled']} 50%, ${theme.colors['background-brand-notification-success']} 50%)`
                    : theme.colors[
                          'background-brand-notification-error'
                      ]} !important;
            }
            &[aria-pressed='true'] {
                background: ${displayBlockDatesFeature
                    ? isSelectedDateInBlockedDates
                        ? isStr
                            ? `linear-gradient(135deg, ${theme.colors['background-brand-fields-disabled']} 50%, ${theme.colors['$04_teal_tint_dark']} 50%)`
                            : theme.colors[
                                  'background-brand-notification-success'
                              ]
                        : `${theme.colors['$04_red_tint_dark']}`
                    : 'inherit'} !important;
            }
        }

        .booking-date-from {
            &[aria-pressed='true'] {
                background: ${isSelectedDateInBlockedDates
                    ? isStr
                        ? isDateFromInCheckins
                            ? `linear-gradient(135deg, transparent 50%, ${theme.colors['$04_teal_tint_dark']} 50%)`
                            : `linear-gradient(135deg, ${theme.colors['background-brand-notification-error']} 50%, ${theme.colors['$04_teal_tint_dark']} 50%)`
                        : `${theme.colors['$04_teal_tint_dark']}`
                    : isStr
                    ? `linear-gradient(135deg, inherit 50%, ${theme.colors['$04_red_tint_dark']} 50%)`
                    : `${theme.colors['$04_red_tint_dark']}`} !important;
            }
        }

        .booking-availability-checkouts {
            background: linear-gradient(
                135deg,
                ${theme.colors['background-brand-disabled']} 50%,
                transparent 50%
            ) !important;

            &:hover {
                background: linear-gradient(
                    135deg,
                    ${theme.colors['background-brand-disabled']} 50%,
                    ${isSelectedDateInBlockedDates
                            ? `${theme.colors['background-brand-notification-success']}`
                            : `${theme.colors['background-brand-notification-error']}`}
                        50%
                ) !important;
            }

            &[aria-pressed='true'] {
                background: linear-gradient(
                    135deg,
                    ${theme.colors['background-brand-disabled']} 50%,
                    ${isSelectedDateInBlockedDates
                            ? `${theme.colors['$04_teal_tint_dark']}`
                            : `${theme.colors['$04_red_tint_dark']}`}
                        50%
                ) !important;
            }
        }

        .booking-availability-checkins {
            background: linear-gradient(
                135deg,
                transparent 50%,
                ${theme.colors['background-brand-disabled']} 50%
            ) !important;

            &:hover {
                background: linear-gradient(
                    135deg,
                    ${!isSelectedDateInBlockedDates
                            ? `${theme.colors['background-brand-notification-error']}`
                            : `${theme.colors['background-brand-notification-success']}`}
                        50%,
                    ${theme.colors['background-brand-disabled']} 50%
                ) !important;
            }

            &[aria-pressed='true'] {
                background: linear-gradient(
                    135deg,
                    ${!isSelectedDateInBlockedDates
                            ? `${theme.colors['$04_red_tint_dark']}`
                            : `${theme.colors['$04_teal_tint_dark']}`}
                        50%,
                    ${theme.colors['background-brand-disabled']} 50%
                ) !important;
            }
        }

        .booking-checkouts-with-blocked-dates {
            background: linear-gradient(
                135deg,
                ${theme.colors['background-brand-disabled']} 50%,
                ${theme.colors['background-brand-notification-error']} 50%
            ) !important;

            &:hover {
                background: linear-gradient(
                    135deg,
                    ${theme.colors['background-brand-disabled']} 50%,
                    ${theme.colors['background-brand-notification-success']} 50%
                ) !important;
            }
        }

        .booking-checkins-with-blocked-dates {
            background: linear-gradient(
                135deg,
                ${theme.colors['background-brand-notification-error']} 50%,
                ${theme.colors['background-brand-disabled']} 50%
            ) !important;
        }
    `}
`;

export const Wrapper = styled.div`
    position: relative;
`;

export const ClearDates = styled(Button)`
    font-size: ${({ theme }) => theme.fontSizes[14]};
`;

export const formatWeekdayName: DateFormatter = (weekDay, options) =>
    format(weekDay, 'EEEEE', { locale: options?.locale });

export const modifiersStyles = (
    isDateInBlockedDates = false,
    isStr = false
) => {
    return {
        today: {
            border: '2px solid #3A77FF'
        },
        dateFrom: {
            background: isDateInBlockedDates
                ? isStr
                    ? 'linear-gradient(135deg, #FFD6C9 50%, #5AECE4 50%)'
                    : '#5AECE4'
                : isStr
                ? 'linear-gradient(135deg, #FAFBFB 50%, #FF8169 50%)'
                : '#FF8169 !important',
            borderRadius: 0,
            borderTopLeftRadius: isStr ? 0 : '50%',
            borderBottomLeftRadius: isStr ? 0 : '50%'
        },
        dateTo: {
            background: isDateInBlockedDates
                ? isStr
                    ? 'linear-gradient(135deg, #5AECE4 50%, #FFD6C9 50%)'
                    : '#5AECE4'
                : isStr
                ? 'linear-gradient(135deg, #FF8169 50%, #FAFBFB 50%)'
                : '#FF8169',
            borderRadius: 0,
            borderTopRightRadius: isStr ? 0 : '50%',
            borderBottomRightRadius: isStr ? 0 : '50%'
        },
        availabilityCheckouts: {
            background: 'linear-gradient(135deg, #5AECE4 50%, #5AECE4 50%)'
        }
    };
};
